import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Send, ScanSearch, Settings, Plus, Sparkles,
  Brain, Search, Database, Layers, FileText, Loader2,
  Bell, CheckCircle2, User, Flag, AlertCircle, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://support-ai-gctw.onrender.com');
const isTestMode = window.location.pathname.replace(/\/$/, '') === '/test';

if (isTestMode) {
  console.log('🧪 Zuppler Sage AI is running in TEST MODE. Unanswered questions will not trigger Zoho tickets or appear in the unanswered list.');
}

const FALLBACK_VAPID_PUBLIC_KEY = 'BDGJ24KqUreodlR8DvQysXVJPt3ukDD_LkKYGuC4T152uZlNrSAmIHiFcrFYAyThUS-V5j8GXi_ce23pNm8XnlU';

function urlBase64ToUint8Array(base64String) {
  if (!base64String) return new Uint8Array(0);
  const clean = String(base64String).trim().replace(/['"]/g, '');
  const padding = '='.repeat((4 - (clean.length % 4)) % 4);
  const base64 = (clean + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function Chat({ onShowAdmin }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [backendReady, setBackendReady] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState(() => {
    const saved = localStorage.getItem('sage_answered_questions');
    return saved ? JSON.parse(saved) : [];
  });
  // Phase 8: Report an issue with response states
  const [reportingIssueIndex, setReportingIssueIndex] = useState(null);
  const [issueTextMap, setIssueTextMap] = useState({});
  const [issueTagMap, setIssueTagMap] = useState({});
  const [submittingIssueMap, setSubmittingIssueMap] = useState({});
  const [reportedSubmittedMap, setReportedSubmittedMap] = useState({});
  const [sessionId] = useState(() => window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15));
  const [showAdminPrompt, setShowAdminPrompt] = useState(false); // NEW for custom admin prompt
  const [adminPassword, setAdminPassword] = useState(''); // NEW for custom admin prompt

  // Phase 7: User Escalation & Push Notification States
  const [userName, setUserName] = useState(() => {
    try {
      return localStorage.getItem('sage_user_name') || '';
    } catch (e) {
      return '';
    }
  });
  const [nameInputs, setNameInputs] = useState({});
  const [editingNameMap, setEditingNameMap] = useState({});
  const [pushPermission, setPushPermission] = useState(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission;
    }
    return 'unsupported';
  });
  const [isPushSubscribed, setIsPushSubscribed] = useState(() => {
    try {
      return !!localStorage.getItem('sage_push_subscription');
    } catch (e) {
      return false;
    }
  });
  const [subscribingPush, setSubscribingPush] = useState(false);
  const [escalationLiveStatus, setEscalationLiveStatus] = useState({});

  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const checkBackend = async () => {
    try {
      const res = await fetch(`${API_URL}/`);
      if (res.ok) {
        setBackendReady(true);
        setIsConnecting(false);
      }
    } catch (err) {
      setTimeout(checkBackend, 2000);
    }
  };

  const handleActivity = () => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    if (timeSinceLastActivity > 10 * 60 * 1000) {
      setIsConnecting(true);
      setBackendReady(false);
      checkBackend();
    }

    lastActivityRef.current = now;
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    checkBackend();

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */


  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    localStorage.setItem('sage_answered_questions', JSON.stringify(answeredQuestions));
  }, [answeredQuestions]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [inputMessage]);

  const handleAskAgain = (questionText, idToRemove) => {
    setAnsweredQuestions(prev => prev.filter(q => q.id !== idToRemove));
    handleSend(questionText);
  };

  const handleDismiss = (idToRemove) => {
    setAnsweredQuestions(prev => prev.filter(q => q.id !== idToRemove));
  };

  const handleSend = async (textOverride = null) => {
    const messageToUse = typeof textOverride === 'string' ? textOverride : inputMessage;

    if (!messageToUse.trim() || loading) return;

    if (!hasInteracted) setHasInteracted(true);

    const userMessage = { role: 'user', content: messageToUse };
    setMessages(prev => [...prev, userMessage]);
    const currentQuestion = messageToUse;

    // Only clear input if we were using the input box
    if (typeof textOverride !== 'string') {
      setInputMessage('');
    }
    setLoading(true);
    setCurrentStep('understanding');

    let overrideQuestion = null;

    const questionToProcess = overrideQuestion || currentQuestion;

    try {
      const conversationHistory = messages.slice(-6).map(msg => ({
        role: msg.role === 'user' ? 'user' : 'assistant',
        content: msg.role === 'user' ? msg.content : msg.content.split('\n\nAGENT REASONING:')[0].replace('CUSTOMER RESPONSE:\n', ''),
      }));

      const res = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: questionToProcess,
          conversationHistory: conversationHistory,
          session_id: sessionId,
          is_test: isTestMode,
        }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('SSE event:', data);

              if (data.error) {
                setMessages(prev => [
                  ...prev,
                  { role: 'error', content: 'Failed to get response. Please try again.' },
                ]);
                setLoading(false);
                setCurrentStep(null);
                return;
              }

              if (data.step) {
                setCurrentStep(data.step);
              }

              if (data.token) {
                accumulatedContent += data.token;
                const currentContent = accumulatedContent;

                setLoading(false);
                setCurrentStep(null);
                // eslint-disable-next-line no-loop-func
                setMessages(prev => {
                  // Check if we already have an assistant message for this question
                  const lastMsg = prev[prev.length - 1];
                  if (lastMsg && lastMsg.role === 'assistant' && lastMsg.question === currentQuestion) {
                    // Update existing message
                    const updated = [...prev];
                    updated[prev.length - 1] = {
                      ...lastMsg,
                      content: currentContent
                    };
                    return updated;
                  } else {
                    // Create new message
                    return [...prev, {
                      role: 'assistant',
                      content: currentContent,
                      question: currentQuestion
                    }];
                  }
                });
              }

              if (data.done) {
                setMessages(prev => {
                  const updated = [...prev];
                  const lastIdx = prev.length - 1;
                  updated[lastIdx] = {
                    ...updated[lastIdx],
                    sources: data.sources,
                    used_source: data.used_source,
                    needs_escalation: data.needs_escalation,
                    topSimilarity: data.top_similarity,
                    unanswered_id: data.unanswered_id,
                    chat_log_id: data.chat_log_id,
                    resolved_question: data.resolved_question,
                    retrieval_confidence: data.retrieval_confidence,
                    matched_documents: data.matched_documents,
                  };
                  return updated;
                });

                if (data.needs_escalation && data.unanswered_id) {
                  try {
                    const pending = JSON.parse(localStorage.getItem('sage_pending_questions') || '[]');
                    if (!pending.find(p => p.id === data.unanswered_id)) {
                      pending.push({ id: data.unanswered_id, question: currentQuestion, createdAt: Date.now() });
                      localStorage.setItem('sage_pending_questions', JSON.stringify(pending));
                    }
                    const savedName = localStorage.getItem('sage_user_name');
                    const savedSub = localStorage.getItem('sage_push_subscription');
                    if (savedName || savedSub) {
                      fetch(`${API_URL}/unanswered/${data.unanswered_id}/user-info`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          userName: savedName || undefined,
                          pushSubscription: savedSub ? JSON.parse(savedSub) : undefined
                        })
                      }).catch(console.error);
                    }
                  } catch (e) {}
                }
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
    } catch (err) {
      console.error('Stream error:', err);
      setMessages(prev => [
        ...prev,
        { role: 'error', content: 'Failed to get response. Please try again.' },
      ]);
    } finally {
      setLoading(false);
      setCurrentStep(null);
    }
  };

  // Phase 8: Report an issue with response handler
  const handleReportIssue = async (msgIndex) => {
    const msg = messages[msgIndex];
    const text = (issueTextMap[msgIndex] || '').trim();
    const tag = issueTagMap[msgIndex] || 'incorrect_info';
    if (!msg?.chat_log_id || !text) return;

    setSubmittingIssueMap(prev => ({ ...prev, [msgIndex]: true }));

    try {
      // Find preceding context
      const precedingHistory = messages.slice(0, msgIndex).map(m => `${m.role}: ${m.content}`).join('\n');

      await fetch(`${API_URL}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_log_id: msg.chat_log_id,
          feedback_text: text,
          feedback_tag: tag,
          resolved_question: msg.resolved_question || undefined,
          conversation_context: precedingHistory || null,
        })
      });

      setReportedSubmittedMap(prev => ({ ...prev, [msgIndex]: true }));
      setReportingIssueIndex(null);
    } catch (err) {
      console.error('Failed to submit reported issue:', err);
    } finally {
      setSubmittingIssueMap(prev => ({ ...prev, [msgIndex]: false }));
    }
  };

  const handleNewChat = () => {
    if (messages.length > 0 && window.confirm('Start a new conversation?')) {
      setMessages([]);
      setInputMessage('');
      setHasInteracted(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAdminClick = () => {
    setShowAdminPrompt(true);
  };

  const submitAdminPassword = (e) => {
    e.preventDefault();
    if (adminPassword === 'heisenberg') {
      try { sessionStorage.setItem('admin_authenticated', 'true'); } catch (err) {}
      onShowAdmin();
    } else {
      alert('Incorrect password');
    }
    setShowAdminPrompt(false);
    setAdminPassword('');
  };

  const cancelAdminPrompt = () => {
    setShowAdminPrompt(false);
    setAdminPassword('');
  };

  // Phase 7: Register Service Worker and inspect push capabilities on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPushPermission(Notification.permission);

      if ('serviceWorker' in navigator && 'PushManager' in window) {
        navigator.serviceWorker.register('/sw.js')
          .then(async (reg) => {
            console.log('✓ Service Worker registered:', reg.scope);
            if ('pushManager' in reg) {
              const sub = await reg.pushManager.getSubscription();
              if (sub) {
                setIsPushSubscribed(true);
                try {
                  localStorage.setItem('sage_push_subscription', JSON.stringify(sub));
                } catch (e) {}
              }
            }
          })
          .catch(err => {
            console.warn('Service worker registration failed:', err);
          });
      }
    } else {
      setPushPermission('unsupported');
    }
  }, []);

  // Phase 7: Enable Push Notifications handler
  const handleSubscribePush = async (unansweredId = null) => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      alert('Push notifications are not supported on this browser.');
      return;
    }

    setSubscribingPush(true);
    try {
      const perm = await Notification.requestPermission();
      setPushPermission(perm);
      if (perm !== 'granted') {
        setSubscribingPush(false);
        return;
      }

      // Fetch VAPID public key with fallback
      let pubKey = FALLBACK_VAPID_PUBLIC_KEY;
      try {
        const keyRes = await fetch(`${API_URL}/push/vapid-public-key`);
        if (keyRes.ok) {
          const keyData = await keyRes.json();
          if (keyData.publicKey) {
            pubKey = keyData.publicKey;
          }
        }
      } catch (keyFetchErr) {
        console.warn('Using fallback VAPID public key:', keyFetchErr);
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();

      const serverKey = urlBase64ToUint8Array(pubKey);

      // If a subscription already exists, unsubscribe first to avoid mismatching applicationServerKey errors
      if (subscription) {
        try {
          await subscription.unsubscribe();
        } catch (unsubErr) {
          console.warn('Could not clear previous push subscription:', unsubErr);
        }
      }

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: serverKey
      });

      setIsPushSubscribed(true);
      try {
        localStorage.setItem('sage_push_subscription', JSON.stringify(subscription));
      } catch (e) {}

      // Attach to pending escalations
      const targetIds = unansweredId ? [unansweredId] : JSON.parse(localStorage.getItem('sage_pending_questions') || '[]').map(p => p.id);
      for (const id of targetIds) {
        await fetch(`${API_URL}/unanswered/${id}/user-info`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pushSubscription: subscription,
            userName: userName || localStorage.getItem('sage_user_name') || undefined
          })
        });
      }
    } catch (err) {
      console.error('Failed to enable push notifications:', err);
      alert('Failed to enable push notifications: ' + err.message);
    } finally {
      setSubscribingPush(false);
    }
  };

  // Phase 7: Save user name handler
  const handleSaveUserName = async (unansweredId, inputVal) => {
    const nameToSave = (inputVal !== undefined ? inputVal : nameInputs[unansweredId] || '').trim();
    if (!nameToSave) return;

    setUserName(nameToSave);
    try {
      localStorage.setItem('sage_user_name', nameToSave);
    } catch (e) {}

    setEditingNameMap(prev => ({ ...prev, [unansweredId]: false }));

    if (unansweredId) {
      try {
        const savedSub = localStorage.getItem('sage_push_subscription');
        const pushSub = savedSub ? JSON.parse(savedSub) : undefined;
        await fetch(`${API_URL}/unanswered/${unansweredId}/user-info`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userName: nameToSave,
            pushSubscription: pushSub
          })
        });
      } catch (err) {
        console.error('Failed to update name on escalation:', err);
      }
    }
  };

  // Check localStorage for answered questions and poll live escalation status every 6s
  useEffect(() => {
    const checkAnswered = async () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity > 5 * 60 * 1000) {
        return;
      }

      try {
        const pending = JSON.parse(localStorage.getItem('sage_pending_questions') || '[]');
        if (pending.length === 0) return;

        const ids = pending.map(p => p.id).join(',');
        const res = await fetch(`${API_URL}/unanswered/status?ids=${ids}`);
        if (!res.ok) {
          return;
        }

        const { statuses } = await res.json();
        
        // Update live status cache for in-thread escalation cards
        setEscalationLiveStatus(prev => {
          const next = { ...prev };
          (statuses || []).forEach(s => {
            next[s.id] = s;
          });
          return next;
        });

        const answered = (statuses || []).filter(s => s.status === 'answered');

        if (answered.length > 0) {
          const enriched = answered.map(a => {
            const original = pending.find(p => p.id === a.id);
            return {
              ...a,
              question: original?.question || a.question,
              answer: a.human_resolution?.answer
            };
          });

          setAnsweredQuestions(prev => {
            const newAnswered = enriched.filter(e => !prev.find(p => p.id === e.id));
            if (newAnswered.length > 0) {
              return [...prev, ...newAnswered];
            }
            return prev;
          });

          // Remove answered ones from localStorage
          const remaining = pending.filter(p => !answered.find(a => a.id === p.id));
          localStorage.setItem('sage_pending_questions', JSON.stringify(remaining));
        }
      } catch (err) {
        console.error('Failed to check answered questions:', err);
      }
    };

    checkAnswered();
    const interval = setInterval(checkAnswered, 6000);
    return () => clearInterval(interval);
  }, []);


  if (isConnecting || !backendReady) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#09090b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '32px',
        padding: '16px',
      }}>
        <style>{`
          @keyframes gradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}</style>
        <div style={{
          width: '80px',
          height: '80px',
          background: 'linear-gradient(45deg, #8b5cf6, #3b82f6, #8b5cf6)',
          backgroundSize: '200% 200%',
          animation: 'gradient 3s ease infinite',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <ScanSearch size={40} color="#fff" style={{ animation: 'float 2s ease-in-out infinite' }} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#fafafa', fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
            Connecting to Zuppler Sage
          </p>
          <p style={{ color: '#71717a', fontSize: '14px', animation: 'pulse 2s ease-in-out infinite' }}>
            May take a minute...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#09090b',
      color: '#fafafa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        * {
          -webkit-overflow-scrolling: touch;
          box-sizing: border-box;
        }
        body {
          background-color: #09090b;
          overscroll-behavior: none;
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes stepFade {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .gradient-text {
          background: linear-gradient(90deg, #8b5cf6, #3b82f6, #8b5cf6);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 3s linear infinite;
        }
        @media (max-width: 768px) {
          .chat-container { padding: 16px !important; }
          .input-container { padding: 16px !important; }
          .header { padding: 12px 16px !important; }
          .message-bubble { max-width: 95% !important; }
          .centered-input-wrapper { max-width: 100% !important; padding: 0 16px !important; }
          .flag-textarea { font-size: 14px !important; }
          .flag-buttons { flex-direction: column !important; }
          .flag-buttons button { width: 100% !important; }
        }
      `}</style>

      {/* Gradient Background Effect */}
      <div style={{
        position: 'fixed',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* Header */}
      <div className="header" style={{
        padding: '20px 32px',
        background: 'linear-gradient(to bottom, rgba(9,9,11,0.98) 0%, rgba(9,9,11,0.85) 70%, transparent 100%)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.03)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h1 style={{
            fontSize: 'clamp(22px, 4.5vw, 26px)',
            fontWeight: '700',
            letterSpacing: '-0.02em',
            fontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
            margin: 0,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <div style={{ fontFamily: '"Varela Round", sans-serif', fontWeight: '600' }}>
              <span style={{
                background: 'linear-gradient(90deg, #ffffff, #c4b5fd, #ffffff)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'shimmer 15s linear infinite',
              }}>Zup</span>
              <span style={{ color: '#fb923c' }}>ple</span>
              <span style={{
                background: 'linear-gradient(90deg, #ffffff, #c4b5fd, #ffffff)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'shimmer 70s linear infinite',
              }}>r</span>
            </div>
            <span style={{
              background: 'linear-gradient(90deg, #8b5cf6, #3b82f6, #8b5cf6)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'shimmer 8s linear infinite',
            }}>Sage</span>
            {isTestMode && (
              <span style={{
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                color: '#ef4444',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontFamily: 'monospace',
                marginLeft: '6px',
                verticalAlign: 'middle',
                display: 'inline-block',
                transform: 'translateY(-2px)',
              }}>
                Test Mode
              </span>
            )}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {messages.length > 0 && (
            <button
              onClick={handleNewChat}
              style={{
                backgroundColor: 'rgba(39, 39, 42, 0.5)',
                color: '#a1a1aa',
                border: '1px solid rgba(63, 63, 70, 0.5)',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(63, 63, 70, 0.5)';
                e.currentTarget.style.color = '#fafafa';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(39, 39, 42, 0.5)';
                e.currentTarget.style.color = '#a1a1aa';
              }}
            >
              <Plus size={16} />
              <span style={{ display: window.innerWidth < 400 ? 'none' : 'inline' }}>New</span>
            </button>
          )}
          <button
            onClick={handleAdminClick}
            style={{
              backgroundColor: 'rgba(39, 39, 42, 0.5)',
              color: '#a1a1aa',
              border: '1px solid rgba(63, 63, 70, 0.5)',
              padding: '8px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              backdropFilter: 'blur(10px)',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(63, 63, 70, 0.5)';
              e.currentTarget.style.color = '#fafafa';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(39, 39, 42, 0.5)';
              e.currentTarget.style.color = '#a1a1aa';
            }}
          >
            <Settings size={18} />
          </button>
        </div>
      </div>

      {/* Premium Toast Notification */}
      {answeredQuestions.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          width: '380px',
          zIndex: 1000,
          padding: '20px',
          borderRadius: '16px',
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          animation: 'slideInRight 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          color: '#ecfccb',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                background: 'rgba(16, 185, 129, 0.2)',
                borderRadius: '50%',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <span style={{ fontSize: '16px' }}>🎉</span>
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#4ade80' }}>
                  Question Answered
                </h4>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#a1a1aa' }}>
                  We've added the missing info
                </p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflowY: 'auto' }}>
            {answeredQuestions.slice(0, 3).map((q) => (
              <div key={q.id} style={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                padding: '12px',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  lineHeight: '1.5',
                  color: '#fff',
                  fontStyle: 'italic',
                }}>
                  "{q.question}"
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button
                    onClick={() => handleDismiss(q.id)}
                    style={{
                      background: 'none',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: '#a1a1aa',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={() => handleAskAgain(q.question, q.id)}
                    style={{
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      border: 'none',
                      color: '#fff',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    Ask Again
                  </button>
                </div>
              </div>
            ))}
            {answeredQuestions.length > 3 && (
              <p style={{ margin: '0', fontSize: '12px', color: '#71717a', textAlign: 'center' }}>
                + {answeredQuestions.length - 3} more
              </p>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Chat Messages or Centered Welcome */}
      {!hasInteracted && messages.length === 0 ? (
        // Centered welcome screen with input
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px 16px',
          position: 'relative',
          zIndex: 1,
        }}>
          <div className="centered-input-wrapper" style={{
            textAlign: 'center',
            animation: 'slideUp 0.6s ease-out',
            maxWidth: '700px',
            width: '100%',
          }}>

            <h2 className="gradient-text" style={{
              fontSize: 'clamp(24px, 6vw, 40px)',
              fontWeight: '700',
              marginBottom: '12px',
              letterSpacing: '-0.03em',
            }}>
              Hey, let's get you unstuck
            </h2>
            <p style={{ color: '#71717a', fontSize: 'clamp(14px, 3vw, 16px)', marginBottom: '40px' }}>
              Ask me anything, I speak Zuppler ;)
            </p>

            {/* Centered Input */}
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              backgroundColor: 'rgba(24, 24, 27, 0.8)',
              border: '1px solid rgba(63, 63, 70, 0.5)',
              borderRadius: '50px',
              padding: '6px 6px 6px 20px',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              transition: 'border-color 0.2s, box-shadow 0.2s',
            }}>
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={"Ask a question..."}
                rows={1}
                style={{
                  flex: 1,
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: '12px 16px',
                  color: '#fafafa',
                  fontSize: '15px',
                  resize: 'none',
                  maxHeight: '200px',
                  overflow: 'auto',
                  outline: 'none',
                  lineHeight: '1.5',
                }}
              />
              <button
                onClick={handleSend}
                disabled={loading || !inputMessage.trim()}
                style={{
                  background: loading || !inputMessage.trim()
                    ? 'rgba(63, 63, 70, 0.5)'
                    : 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                  color: loading || !inputMessage.trim() ? '#71717a' : '#fff',
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '20px',
                  cursor: loading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  flexShrink: 0,
                }}
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Regular chat view
        <>
          <div className="chat-container" style={{
            flex: 1,
            overflowY: 'auto',
            padding: '32px',
            maxWidth: '900px',
            width: '100%',
            margin: '0 auto',
            position: 'relative',
            zIndex: 1,
          }}>

            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  marginBottom: '32px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  animation: 'slideUp 0.4s ease-out',
                }}
              >
                <div className="message-bubble" style={{
                  padding: '16px 20px',
                  borderRadius: '16px',
                  maxWidth: '85%',
                  backgroundColor: msg.role === 'user'
                    ? 'rgba(139, 92, 246, 0.1)'
                    : 'rgba(39, 39, 42, 0.5)',
                  border: msg.role === 'user'
                    ? '1px solid rgba(139, 92, 246, 0.2)'
                    : '1px solid rgba(63, 63, 70, 0.5)',
                  backdropFilter: 'blur(10px)',
                  wordBreak: 'break-word',
                }}>
                  {msg.role === 'assistant' ? (
                    <div style={{
                      fontSize: '15px',
                      lineHeight: '1.7',
                      color: '#fafafa',
                    }}>
                      <ReactMarkdown
                        components={{
                          p: ({ node, ...props }) => <p style={{ margin: '0 0 16px 0', lastChild: { margin: 0 } }} {...props} />,
                          a: ({ node, children, ...props }) => <a style={{ color: '#60a5fa', textDecoration: 'none' }} {...props}>{children}</a>,
                          ul: ({ node, ...props }) => <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px' }} {...props} />,
                          ol: ({ node, ...props }) => <ol style={{ margin: '0 0 16px 0', paddingLeft: '20px' }} {...props} />,
                          li: ({ node, ...props }) => <li style={{ marginBottom: '8px' }} {...props} />,
                          code: ({ node, inline, className, children, ...props }) => {
                            return inline ? (
                              <code style={{
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                padding: '2px 4px',
                                borderRadius: '4px',
                                fontFamily: 'monospace',
                                fontSize: '0.9em'
                              }} {...props}>
                                {children}
                              </code>
                            ) : (
                              <div style={{
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                padding: '12px',
                                borderRadius: '8px',
                                overflowX: 'auto',
                                margin: '0 0 16px 0'
                              }}>
                                <code style={{ fontFamily: 'monospace', fontSize: '0.9em', display: 'block' }} {...props}>
                                  {children}
                                </code>
                              </div>
                            );
                          },
                          h1: ({ node, children, ...props }) => <h3 style={{ fontSize: '1.2em', fontWeight: '600', margin: '20px 0 12px 0' }} {...props}>{children}</h3>,
                          h2: ({ node, children, ...props }) => <h4 style={{ fontSize: '1.1em', fontWeight: '600', margin: '20px 0 12px 0' }} {...props}>{children}</h4>,
                          h3: ({ node, children, ...props }) => <h5 style={{ fontSize: '1em', fontWeight: '600', margin: '20px 0 12px 0' }} {...props}>{children}</h5>,
                          blockquote: ({ node, ...props }) => (
                            <blockquote style={{
                              borderLeft: '4px solid rgba(255,255,255,0.2)',
                              margin: '0 0 16px 0',
                              paddingLeft: '16px',
                              fontStyle: 'italic',
                              color: '#d4d4d8'
                            }} {...props} />
                          ),
                        }}
                      >
                        {msg.content.split('\n\nAGENT REASONING:')[0].split('\n\nSOURCES_USED:')[0].replace('CUSTOMER RESPONSE:\n', '')}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p style={{
                      whiteSpace: 'pre-wrap',
                      fontSize: '15px',
                      lineHeight: '1.7',
                      margin: 0,
                      color: '#fafafa',
                    }}>
                      {msg.content.split('\n\nAGENT REASONING:')[0].split('\n\nSOURCES_USED:')[0].replace('CUSTOMER RESPONSE:\n', '')}
                    </p>
                  )}

                  {isTestMode && msg.role === 'assistant' && (
                    <div style={{
                      marginTop: '16px',
                      padding: '16px',
                      backgroundColor: 'rgba(24, 24, 27, 0.7)',
                      borderRadius: '12px',
                      border: '1px solid rgba(63, 63, 70, 0.7)',
                      fontSize: '13px',
                      color: '#d4d4d8',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '12px',
                        color: '#ef4444',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: '11px',
                        letterSpacing: '0.05em'
                      }}>
                        <span>🧪</span> Test Mode Diagnostics
                      </div>
                      
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '12px',
                        marginBottom: '16px',
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(63, 63, 70, 0.3)'
                      }}>
                        <div>
                          <span style={{ color: '#a1a1aa', display: 'block', fontSize: '11px', marginBottom: '2px' }}>Retrieval Confidence</span>
                          <span style={{
                            fontWeight: 'bold',
                            color: msg.retrieval_confidence === 'HIGH' ? '#10b981' : msg.retrieval_confidence === 'MEDIUM' ? '#f59e0b' : '#ef4444'
                          }}>
                            {msg.retrieval_confidence || 'LOW'}
                          </span>
                        </div>
                        <div>
                          <span style={{ color: '#a1a1aa', display: 'block', fontSize: '11px', marginBottom: '2px' }}>Top Similarity Score</span>
                          <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#f4f4f5' }}>
                            {msg.topSimilarity ? parseFloat(msg.topSimilarity).toFixed(3) : '0.000'}
                          </span>
                        </div>
                        <div>
                          <span style={{ color: '#a1a1aa', display: 'block', fontSize: '11px', marginBottom: '2px' }}>Matched Documents</span>
                          <span style={{ fontWeight: 'bold', color: '#f4f4f5' }}>
                            {msg.matched_documents || 0}
                          </span>
                        </div>
                        <div>
                          <span style={{ color: '#a1a1aa', display: 'block', fontSize: '11px', marginBottom: '2px' }}>Escalation Status</span>
                          <span style={{
                            fontWeight: 'bold',
                            color: msg.needs_escalation ? '#f97316' : '#10b981'
                          }}>
                            {msg.needs_escalation ? '⚠️ Escalated (Unanswered)' : '✅ Answered'}
                          </span>
                        </div>
                      </div>

                      {/* Explanation of why it was answered or escalated */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontWeight: 'bold', color: '#fafafa', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>🔍</span> Decision Logic:
                        </div>
                        <div style={{
                          backgroundColor: 'rgba(39, 39, 42, 0.4)',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          lineHeight: '1.5',
                          color: '#e4e4e7'
                        }}>
                          {msg.needs_escalation ? (
                            <span>
                              <strong>Escalation Triggered:</strong> The AI detected escalation cues in the response (e.g. recommending to contact support) or the similarity score was too low. 
                              {msg.retrieval_confidence === 'LOW' && " Specifically, the retrieval confidence was LOW, indicating no matching documents were found in the knowledge base."}
                              {msg.retrieval_confidence === 'MEDIUM' && " The retrieval confidence was MEDIUM, meaning documents were found but they may not have fully answered the query."}
                              <br /><em style={{ color: '#a1a1aa', fontSize: '12px' }}>(In normal mode, this would insert a row in unanswered_questions and trigger a Zoho Desk ticket.)</em>
                            </span>
                          ) : (
                            <span>
                              <strong>Directly Answered:</strong> The query was resolved using the retrieved knowledge base documents. The confidence was <strong>{msg.retrieval_confidence || 'HIGH'}</strong> and no escalation patterns were matched.
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Internal AI Reasoning */}
                      {msg.content.includes('\n\nAGENT REASONING:') && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontWeight: 'bold', color: '#fafafa', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>🧠</span> Internal Agent Reasoning:
                          </div>
                          <div style={{
                            backgroundColor: 'rgba(139, 92, 246, 0.1)',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            borderLeft: '3px solid #8b5cf6',
                            fontStyle: 'italic',
                            lineHeight: '1.5',
                            color: '#e4e4e7',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {msg.content.split('\n\nAGENT REASONING:')[1]?.split('\n\nSOURCES_USED:')[0]?.trim()}
                          </div>
                        </div>
                      )}

                      {/* All Matched Sources / What was found */}
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#fafafa', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>📚</span> Retrieved Knowledge Sources:
                        </div>
                        {msg.sources && msg.sources.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {msg.sources.map((src, idx) => {
                              const isUsed = msg.content.includes('\n\nSOURCES_USED:') && 
                                msg.content.split('\n\nSOURCES_USED:')[1]?.toLowerCase().includes(src.name.toLowerCase());
                              return (
                                <div key={idx} style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '6px 10px',
                                  backgroundColor: isUsed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(39, 39, 42, 0.2)',
                                  borderRadius: '6px',
                                  border: isUsed ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(63, 63, 70, 0.2)',
                                }}>
                                  <span style={{
                                    color: isUsed ? '#34d399' : '#a1a1aa',
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '75%'
                                  }}>
                                    {src.name} {isUsed && <span style={{ fontSize: '10px', color: '#34d399', fontWeight: 'bold', marginLeft: '4px' }}>(USED)</span>}
                                  </span>
                                  {src.url ? (
                                    <a href={src.url} target="_blank" rel="noopener noreferrer" style={{
                                      color: '#60a5fa',
                                      textDecoration: 'none',
                                      fontSize: '11px'
                                    }}>
                                      View Source →
                                    </a>
                                  ) : (
                                    <span style={{ color: '#71717a', fontSize: '11px' }}>No link</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ color: '#71717a', fontStyle: 'italic', fontSize: '12px' }}>
                            No relevant documents were retrieved from the knowledge base.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {msg.used_source && (msg.used_source.url || msg.used_source.content) && (
                    <div style={{
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '1px solid rgba(63, 63, 70, 0.3)',
                    }}>
                      {(msg.used_source.url && (msg.used_source.url.toLowerCase().includes('youtube.com') || msg.used_source.url.toLowerCase().includes('youtu.be') || msg.used_source.url.toLowerCase().includes('vimeo'))) ? (
                        <div style={{ width: '100%', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(63, 63, 70, 0.5)', marginTop: '8px' }}>
                          <iframe
                            width="100%"
                            height="240"
                            src={msg.used_source.url.toLowerCase().includes('vimeo') 
                              ? (msg.used_source.url.includes('player.vimeo.com/video/') ? msg.used_source.url : `https://player.vimeo.com/video/${msg.used_source.url.toLowerCase().split('vimeo.com/')[1]?.split('?')[0]?.replace('video/', '')}`) 
                              : `https://www.youtube.com/embed/${msg.used_source.url.split('v=')[1]?.split('&')[0] || msg.used_source.url.split('youtu.be/')[1]?.split('?')[0]}`
                            }
                            title="Video Citation"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ display: 'block' }}
                          ></iframe>
                          <div style={{ padding: '8px 12px', backgroundColor: 'rgba(24, 24, 27, 0.8)', fontSize: '12px', color: '#a1a1aa' }}>
                            Source: {msg.used_source.name}
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <a
                            href={msg.used_source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '12px',
                              color: '#8b5cf6',
                              textDecoration: 'none',
                              backgroundColor: 'rgba(139, 92, 246, 0.1)',
                              border: '1px solid rgba(139, 92, 246, 0.3)',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s',
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.2)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            📖 More information
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                              <polyline points="15 3 21 3 21 9"></polyline>
                              <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                { msg.needs_escalation && (() => {
                  const qId = msg.unanswered_id;
                  const liveInfo = qId ? escalationLiveStatus[qId] : null;
                  const isResolved = liveInfo?.status === 'answered' || !!liveInfo?.human_resolution?.answer;
                  const isDismissed = liveInfo?.status === 'dismissed';
                  const expertName = liveInfo?.assigned_expert?.name || (typeof liveInfo?.assigned_expert === 'string' ? liveInfo.assigned_expert : null);
                  const currentNameVal = nameInputs[qId] !== undefined ? nameInputs[qId] : userName;
                  const isEditingName = editingNameMap[qId];

                  return (
                    <div style={{
                      marginTop: '12px',
                      width: '100%',
                      maxWidth: '85%',
                      borderRadius: '12px',
                      border: isResolved ? '1px solid rgba(34, 197, 94, 0.35)' : '1px solid rgba(139, 92, 246, 0.25)',
                      background: isResolved 
                        ? 'linear-gradient(135deg, rgba(6, 78, 59, 0.3) 0%, rgba(24, 24, 27, 0.85) 100%)' 
                        : 'linear-gradient(135deg, rgba(30, 27, 75, 0.35) 0%, rgba(24, 24, 27, 0.85) 100%)',
                      padding: '16px 18px',
                      backdropFilter: 'blur(12px)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                      animation: 'fadeIn 0.25s ease-out',
                    }}>
                      {/* Header Row */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: isResolved ? 'rgba(34, 197, 94, 0.15)' : 'rgba(139, 92, 246, 0.15)',
                            border: isResolved ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(139, 92, 246, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: isResolved ? '#4ade80' : '#c084fc',
                            flexShrink: 0,
                          }}>
                            {isResolved ? <CheckCircle2 size={16} /> : <Sparkles size={16} />}
                          </div>
                          <div>
                            <h4 style={{
                              margin: '0 0 2px 0',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#ffffff',
                              letterSpacing: '-0.01em',
                            }}>
                              {isResolved ? "Answer Added to Sage!" : "We don't have this recorded yet!"}
                            </h4>
                            <p style={{ margin: 0, fontSize: '12px', color: '#a1a1aa', lineHeight: '1.4' }}>
                              {isResolved
                                ? `Team verified and documented the answer below.`
                                : isDismissed
                                ? `This inquiry was reviewed and marked complete.`
                                : `Tech team has been notified. Leave your name or turn on alerts so you're the first to know when it's added!`}
                            </p>
                          </div>
                        </div>

                        {/* Status Pill */}
                        <div style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          backgroundColor: isResolved ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                          color: isResolved ? '#4ade80' : '#fbbf24',
                          border: isResolved ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(245, 158, 11, 0.3)',
                          flexShrink: 0,
                        }}>
                          {isResolved ? (
                            <>
                              <CheckCircle2 size={12} />
                              <span>Answered</span>
                            </>
                          ) : isDismissed ? (
                            <span>Reviewed</span>
                          ) : (
                            <>
                              <span style={{
                                width: '5px',
                                height: '5px',
                                borderRadius: '50%',
                                backgroundColor: '#fbbf24',
                                boxShadow: '0 0 6px #fbbf24',
                                animation: 'pulse 1.5s infinite',
                              }} />
                              <span>Tech Notified</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Compact Progress Stepper */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '6px',
                        backgroundColor: 'rgba(0, 0, 0, 0.25)',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.04)',
                        overflowX: 'auto',
                      }}>
                        {/* Step 1 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                          <span style={{ color: '#4ade80', fontSize: '11px', fontWeight: 'bold' }}>✓</span>
                          <span style={{ fontSize: '11px', color: '#d4d4d8' }}>Received</span>
                        </div>
                        <span style={{ color: '#52525b', fontSize: '10px' }}>›</span>

                        {/* Step 2 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                          <span style={{ color: '#4ade80', fontSize: '11px', fontWeight: 'bold' }}>✓</span>
                          <span style={{ fontSize: '11px', color: '#d4d4d8' }}>
                            {expertName ? `Notified (${expertName.split(' ')[0]})` : 'Tech Notified'}
                          </span>
                        </div>
                        <span style={{ color: '#52525b', fontSize: '10px' }}>›</span>

                        {/* Step 3 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                          <span style={{ color: isResolved ? '#4ade80' : '#fbbf24', fontSize: '11px', fontWeight: 'bold' }}>
                            {isResolved ? '✓' : '⏳'}
                          </span>
                          <span style={{ fontSize: '11px', color: isResolved ? '#d4d4d8' : '#fbbf24', fontWeight: isResolved ? 'normal' : '500' }}>
                            {isResolved ? 'Documented' : 'Pending Review'}
                          </span>
                        </div>
                        <span style={{ color: '#52525b', fontSize: '10px' }}>›</span>

                        {/* Step 4 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                          <span style={{ color: isResolved ? '#4ade80' : '#71717a', fontSize: '11px', fontWeight: 'bold' }}>
                            {isResolved ? '✓' : '•'}
                          </span>
                          <span style={{ fontSize: '11px', color: isResolved ? '#4ade80' : '#71717a', fontWeight: isResolved ? '600' : 'normal' }}>
                            Ready
                          </span>
                        </div>
                      </div>

                      {/* Verified Answer Section (Live When Resolved) */}
                      {isResolved && liveInfo?.human_resolution?.answer && (
                        <div style={{
                          backgroundColor: 'rgba(24, 24, 27, 0.75)',
                          border: '1px solid rgba(34, 197, 94, 0.35)',
                          borderRadius: '8px',
                          padding: '12px 14px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#4ade80', display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <CheckCircle2 size={13} /> Official Answer
                            </span>
                            {liveInfo?.human_resolution?.resolved_by && (
                              <span style={{ fontSize: '10px', color: '#a1a1aa' }}>
                                by {liveInfo.human_resolution.resolved_by}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '13px', lineHeight: '1.5', color: '#fafafa' }}>
                            <ReactMarkdown>{liveInfo.human_resolution.answer}</ReactMarkdown>
                          </div>
                          {liveInfo?.human_resolution?.target_document && (
                            <div style={{ fontSize: '10px', color: '#71717a', fontStyle: 'italic' }}>
                              📚 Added to: {liveInfo.human_resolution.target_document}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Name Context Block ("Who should we say is asking?") */}
                      {!isResolved && (
                        <div>
                          {userName && !isEditingName ? (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              backgroundColor: 'rgba(0, 0, 0, 0.2)',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 255, 255, 0.04)',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#d4d4d8' }}>
                                <User size={13} color="#8b5cf6" />
                                <span>Asking as: <strong style={{ color: '#ffffff' }}>{userName}</strong></span>
                              </div>
                              <button
                                onClick={() => setEditingNameMap(prev => ({ ...prev, [qId]: true }))}
                                style={{ background: 'none', border: 'none', color: '#a1a1aa', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                              >
                                (change)
                              </button>
                            </div>
                          ) : (
                            <div style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.2)',
                              padding: '10px 12px',
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 255, 255, 0.04)',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <User size={13} color="#8b5cf6" />
                                <span style={{ fontSize: '12px', fontWeight: '500', color: '#ffffff' }}>Who should we say is asking?</span>
                              </div>
                              <p style={{ margin: 0, fontSize: '11px', color: '#a1a1aa', lineHeight: '1.3' }}>
                                Your name helps the team follow up if needed.
                              </p>
                              <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                                <input
                                  type="text"
                                  placeholder="Your name or email..."
                                  value={currentNameVal}
                                  onChange={(e) => setNameInputs(prev => ({ ...prev, [qId]: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveUserName(qId, currentNameVal);
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: '6px 10px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(63, 63, 70, 0.6)',
                                    backgroundColor: 'rgba(9, 9, 11, 0.7)',
                                    color: '#ffffff',
                                    fontSize: '12px',
                                    outline: 'none',
                                  }}
                                />
                                <button
                                  onClick={() => handleSaveUserName(qId, currentNameVal)}
                                  style={{
                                    padding: '6px 14px',
                                    backgroundColor: '#8b5cf6',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    flexShrink: 0,
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#8b5cf6'}
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Push Notification Toggle & State */}
                      {!isResolved && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '10px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          backgroundColor: isPushSubscribed && pushPermission === 'granted'
                            ? 'rgba(34, 197, 94, 0.08)'
                            : 'rgba(139, 92, 246, 0.08)',
                          border: isPushSubscribed && pushPermission === 'granted'
                            ? '1px solid rgba(34, 197, 94, 0.2)'
                            : '1px solid rgba(139, 92, 246, 0.2)',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Bell size={13} color={isPushSubscribed && pushPermission === 'granted' ? '#4ade80' : '#c084fc'} />
                            <span style={{ fontSize: '11px', color: '#d4d4d8' }}>
                              {pushPermission === 'denied'
                                ? 'Browser alerts blocked in address bar'
                                : isPushSubscribed && pushPermission === 'granted'
                                ? 'Alerts active — you will be notified when answered'
                                : 'Get notified as soon as an answer is added'}
                            </span>
                          </div>

                          <div>
                            {pushPermission === 'denied' ? (
                              <span style={{ fontSize: '11px', color: '#f97316' }}>
                                🔕 Blocked
                              </span>
                            ) : isPushSubscribed && pushPermission === 'granted' ? (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '11px',
                                fontWeight: '500',
                                color: '#4ade80',
                              }}>
                                <CheckCircle2 size={12} /> Active
                              </div>
                            ) : (
                              <button
                                onClick={() => handleSubscribePush(qId)}
                                disabled={subscribingPush}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  backgroundColor: 'rgba(139, 92, 246, 0.2)',
                                  color: '#c084fc',
                                  border: '1px solid rgba(139, 92, 246, 0.4)',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  opacity: subscribingPush ? 0.7 : 1,
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.35)';
                                  e.currentTarget.style.color = '#ffffff';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.2)';
                                  e.currentTarget.style.color = '#c084fc';
                                }}
                              >
                                {subscribingPush ? (
                                  <>
                                    <Loader2 size={11} className="animate-spin" /> Subscribing...
                                  </>
                                ) : (
                                  <>
                                    <Bell size={11} /> Turn on alerts
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {msg.role === 'assistant' && !msg.needs_escalation && msg.chat_log_id && (
                  <div style={{ marginTop: '8px', width: '100%', maxWidth: '85%' }}>
                    {!reportedSubmittedMap[i] && reportingIssueIndex !== i && (
                      <button
                        onClick={() => setReportingIssueIndex(i)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          color: '#71717a',
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = '#f87171';
                          e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color = '#71717a';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <Flag size={12} /> Report an issue with this response
                      </button>
                    )}

                    {/* Report Form Popover */}
                    {reportingIssueIndex === i && (
                      <div style={{
                        marginTop: '8px',
                        padding: '14px',
                        backgroundColor: 'rgba(24, 24, 27, 0.9)',
                        borderRadius: '10px',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        animation: 'fadeIn 0.2s ease-out',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#fca5a5', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <AlertCircle size={14} /> Report an issue with this response
                          </span>
                          <button
                            onClick={() => setReportingIssueIndex(null)}
                            style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', padding: '2px' }}
                          >
                            <X size={14} />
                          </button>
                        </div>

                        {/* Quick category tags */}
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {[
                            { id: 'incorrect_info', label: 'Incorrect Info' },
                            { id: 'outdated', label: 'Outdated Info' },
                            { id: 'missing_steps', label: 'Missing Steps' },
                            { id: 'confusing', label: 'Confusing' }
                          ].map(tag => (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={() => setIssueTagMap(prev => ({ ...prev, [i]: tag.id }))}
                              style={{
                                padding: '3px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                border: (issueTagMap[i] || 'incorrect_info') === tag.id ? '1px solid #ef4444' : '1px solid rgba(63, 63, 70, 0.5)',
                                backgroundColor: (issueTagMap[i] || 'incorrect_info') === tag.id ? 'rgba(239, 68, 68, 0.15)' : 'transparent',
                                color: (issueTagMap[i] || 'incorrect_info') === tag.id ? '#fca5a5' : '#a1a1aa',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                              }}
                            >
                              {tag.label}
                            </button>
                          ))}
                        </div>

                        <textarea
                          value={issueTextMap[i] || ''}
                          onChange={(e) => setIssueTextMap(prev => ({ ...prev, [i]: e.target.value }))}
                          placeholder="Tell us what is wrong with this answer so our team can diagnose and update the docs..."
                          style={{
                            width: '100%',
                            minHeight: '60px',
                            backgroundColor: 'rgba(9, 9, 11, 0.8)',
                            border: '1px solid rgba(63, 63, 70, 0.8)',
                            borderRadius: '6px',
                            padding: '8px 10px',
                            color: '#ffffff',
                            fontSize: '12px',
                            outline: 'none',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#ef4444'}
                          onBlur={(e) => e.target.style.borderColor = 'rgba(63, 63, 70, 0.8)'}
                        />

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => setReportingIssueIndex(null)}
                            style={{
                              padding: '5px 10px',
                              borderRadius: '6px',
                              background: 'none',
                              border: '1px solid rgba(63, 63, 70, 0.6)',
                              color: '#a1a1aa',
                              fontSize: '12px',
                              cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReportIssue(i)}
                            disabled={!(issueTextMap[i] || '').trim() || submittingIssueMap[i]}
                            style={{
                              padding: '5px 12px',
                              borderRadius: '6px',
                              backgroundColor: (issueTextMap[i] || '').trim() ? '#ef4444' : 'rgba(63, 63, 70, 0.5)',
                              color: '#ffffff',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: (issueTextMap[i] || '').trim() ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            {submittingIssueMap[i] ? (
                              <>
                                <Loader2 size={12} className="animate-spin" /> Submitting...
                              </>
                            ) : (
                              'Submit Report'
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Feedback Submitted Confirmation */}
                    {reportedSubmittedMap[i] && (
                      <div style={{
                        marginTop: '8px',
                        padding: '6px 12px',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid rgba(34, 197, 94, 0.25)',
                        borderRadius: '6px',
                        color: '#4ade80',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        width: 'fit-content',
                      }}>
                        <CheckCircle2 size={13} /> Thank you! Issue reported — our team and AI are diagnosing this.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div style={{
                marginBottom: '28px',
                display: 'flex',
                alignItems: 'flex-start',
                animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              }}>
                <div style={{
                  alignSelf: 'flex-start',
                  backgroundColor: 'rgba(24, 24, 27, 0.75)',
                  backdropFilter: 'blur(16px)',
                  padding: '16px 20px',
                  borderRadius: '4px 20px 20px 20px',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
                  width: '320px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                }}>
                  {(() => {
                    const stepConfigs = [
                      { key: 'understanding', icon: Brain, label: 'Understanding query intent' },
                      { key: 'rewriting', icon: Search, label: 'Optimizing search parameters' },
                      { key: 'searching', icon: Database, label: 'Searching knowledge index' },
                      { key: 'ranking', icon: Layers, label: 'Reranking context relevance' },
                      { key: 'retrieving', icon: FileText, label: 'Retrieving documentation' },
                      { key: 'generating', icon: Sparkles, label: 'Synthesizing response' },
                    ];

                    const rawIndex = stepConfigs.findIndex(s => s.key === currentStep);
                    const currentIndex = rawIndex >= 0 ? rawIndex : 0;
                    const currentStepObj = stepConfigs[currentIndex];
                    const IconComponent = currentStepObj.icon;
                    const totalSteps = stepConfigs.length;

                    return (
                      <>
                        {/* Header / Active Step */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '8px',
                              backgroundColor: 'rgba(139, 92, 246, 0.12)',
                              border: '1px solid rgba(139, 92, 246, 0.25)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#a78bfa',
                              flexShrink: 0,
                            }}>
                              <IconComponent size={14} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#f4f4f5',
                                letterSpacing: '-0.01em',
                                lineHeight: '1.3',
                              }}>
                                {currentStepObj.label}
                              </span>
                              <span style={{ fontSize: '11px', color: '#71717a', fontWeight: '400' }}>
                                Step {currentIndex + 1} of {totalSteps}
                              </span>
                            </div>
                          </div>
                          <Loader2 size={15} style={{ color: '#8b5cf6', animation: 'spin 1.2s linear infinite', flexShrink: 0 }} />
                        </div>

                        {/* Minimalist Progress Track */}
                        <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                          {stepConfigs.map((step, i) => {
                            const isPassed = i <= currentIndex;
                            const isCurrent = i === currentIndex;
                            return (
                              <div
                                key={step.key}
                                style={{
                                  height: '3px',
                                  flex: 1,
                                  borderRadius: '2px',
                                  backgroundColor: isCurrent
                                    ? '#8b5cf6'
                                    : isPassed
                                    ? 'rgba(139, 92, 246, 0.4)'
                                    : 'rgba(255, 255, 255, 0.08)',
                                  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                                  boxShadow: isCurrent ? '0 0 8px rgba(139, 92, 246, 0.6)' : 'none',
                                }}
                              />
                            );
                          })}
                        </div>

                        {/* Micro Skeleton shimmer lines */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', opacity: 0.35 }}>
                          <div style={{
                            height: '8px',
                            width: '85%',
                            borderRadius: '4px',
                            background: 'linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.04) 100%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 2s infinite linear',
                          }} />
                          <div style={{
                            height: '8px',
                            width: '55%',
                            borderRadius: '4px',
                            background: 'linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.04) 100%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 2s infinite linear',
                          }} />
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Input at bottom */}
          <div className="input-container" style={{
            padding: '24px 32px 32px',
            borderTop: '1px solid rgba(39, 39, 42, 0.5)',
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(9, 9, 11, 0.8)',
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
          }}>
            <div style={{
              maxWidth: '900px',
              margin: '0 auto',
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-end',
            }}>
              <div style={{
                flex: 1,
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                backgroundColor: 'rgba(24, 24, 27, 0.8)',
                border: '1px solid rgba(63, 63, 70, 0.5)',
                borderRadius: '50px',
                padding: '6px 6px 6px 20px',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                transition: 'border-color 0.2s, box-shadow 0.2s, background-color 0.3s',
              }}>
                <textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={"Ask a question..."}
                  rows={1}
                  style={{
                    flex: 1,
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: '12px 16px',
                    color: '#fafafa',
                    fontSize: '15px',
                    resize: 'none',
                    maxHeight: '200px',
                    overflow: 'auto',
                    outline: 'none',
                    lineHeight: '1.5',
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={loading || !inputMessage.trim()}
                  style={{
                    background: loading || !inputMessage.trim()
                      ? 'rgba(63, 63, 70, 0.5)'
                      : 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                    color: loading || !inputMessage.trim() ? '#71717a' : '#fff',
                    border: 'none',
                    width: '40px',
                    height: '40px',
                    borderRadius: '20px',
                    cursor: loading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    flexShrink: 0,
                  }}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}


      {/* Custom Admin Prompt Modal */}
      {showAdminPrompt && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(5px)',
        }}>
          <div style={{
            backgroundColor: '#18181b',
            padding: '32px',
            borderRadius: '16px',
            border: '1px solid rgba(63, 63, 70, 0.5)',
            width: '100%',
            maxWidth: '360px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
          }}>
            <h3 style={{ color: '#fafafa', marginTop: 0, marginBottom: '24px', fontSize: '20px', fontWeight: '600' }}>Password:</h3>
            <form onSubmit={submitAdminPassword}>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(9, 9, 11, 0.8)',
                  border: '1px solid rgba(63, 63, 70, 0.5)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '16px',
                  outline: 'none',
                  marginBottom: '24px',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={cancelAdminPrompt}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#a1a1aa',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                    border: 'none',
                    color: '#fff',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Enter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notification Guidance Overlay */}
    </div>
  );
}